import React, { useState, useEffect } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useSignup } from './SignupProvider';
import PricingCalculator from '../PricingCalculator';
import { calculatePricing } from '../../services/stripe';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../services/firebase';
import { isValidTokenFormat, TokenValidationResult } from '../../models/token';

// Initialize Stripe using the service
import { getStripe } from '../../services/stripe';
const stripePromise = getStripe();

const functions = app ? getFunctions(app) : null;
const createRestaurantSignup = functions ? httpsCallable(functions, 'createRestaurantSignup') : null;
const validateTokenFn = functions ? httpsCallable(functions, 'validateToken') : null;

const PaymentForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { state, setError, clearError, goToStep } = useSignup();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  
  // Payment token state
  const [paymentToken, setPaymentToken] = useState('');
  const [tokenValidation, setTokenValidation] = useState<TokenValidationResult>({ isValid: false });
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [cardValidated, setCardValidated] = useState(false);

  // Calculate current pricing
  const currentPricing = state.paymentDetails?.calculation || calculatePricing(
    state.data.tableCount || 1,
    state.data.nfcCount || 1,
    state.data.tabletCount || 1,
    state.data.pricing
  );

  // Validate payment token via Cloud Function
  const validatePaymentToken = async (token: string): Promise<TokenValidationResult> => {
    if (!token || !isValidTokenFormat(token)) {
      return { isValid: false, error: 'Invalid token format' };
    }

    if (!validateTokenFn) {
      return { isValid: false, error: 'Service unavailable' };
    }

    try {
      const result = await validateTokenFn({ token, tokenType: 'payment' });
      return result.data as TokenValidationResult;
    } catch (error) {
      console.error('Token validation error:', error);
      return { isValid: false, error: 'Invalid Token' };
    }
  };

  // Handle payment token input
  const handlePaymentTokenChange = async (token: string) => {
    // Clean token input (alphanumeric only, max 20 chars)
    const cleanToken = token.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
    setPaymentToken(cleanToken);

    // Reset validation state
    setTokenValidation({ isValid: false });

    // Only validate if token is complete (20 characters)
    if (cleanToken.length === 20) {
      setIsValidatingToken(true);
      try {
        const validation = await validatePaymentToken(cleanToken);
        setTokenValidation(validation);
      } catch (error) {
        setTokenValidation({ isValid: false, error: 'Invalid Token' });
      } finally {
        setIsValidatingToken(false);
      }
    }
  };

  // Monitor card element changes for validation
  useEffect(() => {
    if (!elements || !tokenValidation.isValid) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    const handleCardChange = (event: any) => {
      setCardValidated(event.complete && !event.error);
    };

    cardElement.on('change', handleCardChange);

    return () => {
      cardElement.off('change', handleCardChange);
    };
  }, [elements, tokenValidation.isValid]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setPaymentError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError('Card element not found. Please refresh and try again.');
      return;
    }

    // Validate payment token before processing
    if (!tokenValidation.isValid) {
      setPaymentError('Valid payment token is required to complete your purchase.');
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);
    clearError('payment');

    try {
      // Step 1: Create subscription with hardware charges via Firebase Cloud Function
      console.log('Creating subscription with hardware charges...');
      
      if (!functions) {
        throw new Error('Service unavailable. Please try again later.');
      }

      const createSubscriptionWithHardware = httpsCallable(functions, 'createSubscriptionWithHardware');
      const subscriptionResult = await createSubscriptionWithHardware({
        tableCount: state.data.tableCount,
        nfcCount: state.data.nfcCount,
        pricePerTable: currentPricing.pricePerTable,
        pricePerNFC: currentPricing.pricePerNFC,
        currency: 'usd',
        paymentToken: paymentToken, // Include payment token for validation
        customerData: {
          restaurant: state.data.restaurant,
          manager: state.data.manager,
        },
        metadata: {
          tableCount: state.data.tableCount,
          nfcCount: state.data.nfcCount,
          restaurantName: state.data.restaurant?.name,
        },
      });

      const subscriptionData = subscriptionResult.data as any;
      if (!subscriptionData.success) {
        throw new Error(subscriptionData.error || 'Failed to create subscription');
      }

      const { clientSecret, subscriptionId, paymentIntentId: realPaymentIntentId } = subscriptionData.data;
      
      console.log('Subscription created successfully:', subscriptionId);

      // Step 2: Confirm payment with Stripe
      console.log('Processing payment...');
      
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: state.data.manager?.displayName,
            email: state.data.manager?.email,
            phone: state.data.manager?.phoneNumber,
            address: {
              line1: state.data.restaurant?.address,
              city: state.data.restaurant?.city,
              state: state.data.restaurant?.state,
              postal_code: state.data.restaurant?.zipCode,
            },
          },
        },
      });

      if (result.error) {
        throw new Error(result.error.message || 'Payment failed');
      }

      if (result.paymentIntent.status !== 'succeeded') {
        throw new Error('Payment was not completed successfully');
      }

      console.log('Payment successful, creating restaurant account...');

      // Step 3: Call Firebase Cloud Function to create account
      const signupData = {
        restaurant: state.data.restaurant,
        manager: state.data.manager,
        tableCount: state.data.tableCount,
        nfcCount: state.data.nfcCount,
        stripePaymentIntentId: realPaymentIntentId,
        stripeSubscriptionId: subscriptionId,
        stripeCustomerId: subscriptionData.data.customerId,
        pricing: state.data.pricing,
        paymentToken: paymentToken, // Pass payment token for usage tracking
        customSalesToken: state.data.customSalesToken, // Pass custom sales token if used
      };

      if (!createRestaurantSignup) {
        throw new Error('Service unavailable. Please try again later.');
      }

      const accountResult = await createRestaurantSignup(signupData);
      const accountData = accountResult.data as any;
      
      if (accountData.success) {
        console.log('Account created successfully:', accountData.data);
        goToStep('success'); // Go directly to success step, skip processing
      } else {
        throw new Error(accountData.error || 'Account creation failed');
      }

    } catch (error: any) {
      console.error('Payment/signup failed:', error);
      setPaymentError(error.message || 'Payment failed. Please try again.');
      setError('payment', error.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrevious = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    goToStep('tables');
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="payment-step">
      <div className="step-header">
        <h2>Complete Your Payment</h2>
        <p>Secure payment processing powered by Stripe</p>
      </div>

      <div className="payment-content">
        <div className="payment-form-container">
          <form onSubmit={handleSubmit} className="payment-form" noValidate>
            <div className="payment-summary">
              <h3>Order Summary</h3>
              <div className="summary-item">
                <span>Restaurant: {state.data.restaurant?.name}</span>
              </div>
              <div className="summary-item">
                <span>Manager: {state.data.manager?.displayName}</span>
              </div>
              <div className="summary-item">
                <span>TableChat Complete - $379/month</span>
              </div>
            </div>

            <div className="payment-section">
              <h3>Payment Information</h3>
              
              {/* Payment Token Field - Show first, BEFORE card */}
              <div className="payment-token-section">
                <label htmlFor="paymentToken" className="token-label">
                  Payment Token *
                </label>
                <div className="token-input-container">
                  <input
                    type="text"
                    id="paymentToken"
                    value={paymentToken}
                    onChange={(e) => handlePaymentTokenChange(e.target.value)}
                    placeholder="Enter 20-digit payment token from TableChat"
                    className={`token-input ${
                      paymentToken.length === 20 
                        ? tokenValidation.isValid 
                          ? 'valid' 
                          : 'invalid'
                        : ''
                    }`}
                    maxLength={20}
                    disabled={isValidatingToken}
                  />
                  <div className="token-status">
                    {isValidatingToken && (
                      <span className="validating">🔄 Validating...</span>
                    )}
                    {paymentToken.length === 20 && !isValidatingToken && (
                      tokenValidation.isValid ? (
                        <span className="valid-token">✅ Valid Token - You may proceed</span>
                      ) : (
                        <span className="invalid-token">❌ {tokenValidation.error}</span>
                      )
                    )}
                  </div>
                </div>
                <p className="token-help-text">Please enter the payment token provided by TableChat</p>
              </div>

              {/* Card Element - Only show after token is validated */}
              {tokenValidation.isValid && (
                <div className="card-section">
                  <label className="card-label">Credit Card Information *</label>
                  <div className="card-element-container">
                    <CardElement options={cardElementOptions} />
                  </div>
                </div>
              )}
              
              {paymentError && (
                <div className="payment-error">
                  <span className="error-icon">⚠️</span>
                  <span>{paymentError}</span>
                </div>
              )}
            </div>

            <div className="payment-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrevious}
                disabled={isProcessing}
              >
                Back: Table Count
              </button>
              
              <button
                type="submit"
                className="btn btn-primary btn-payment"
                disabled={!stripe || isProcessing || !tokenValidation.isValid || !cardValidated}
                onClick={(e) => {
                  // Let the form's onSubmit handle this
                  if (!stripe || isProcessing || !tokenValidation.isValid || !cardValidated) {
                    e.preventDefault();
                  }
                }}
              >
                {isProcessing ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    Pay $379.00
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="security-notice">
            <div className="security-icons">
              <span>🔒</span>
              <span>💳</span>
            </div>
            <div className="security-text">
              <p><strong>Secure Payment by Stripe</strong></p>
              <p>Your payment information is encrypted and secure. We never store your card details.</p>

              <div className="what-happens-next">
                <h4>What happens next?</h4>
                <ul>
                  <li>✅ Your restaurant account will be created instantly</li>
                  <li>✅ Login credentials active immediately</li>
                  <li>✅ NFC table taps will ship within 2-3 business days</li>
                  <li>✅ Setup support will contact you shortly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="pricing-sidebar">
          <PricingCalculator 
            calculation={currentPricing}
            showBreakdown={true}
            isNegotiated={state.isNegotiatedPricing}
          />
        </div>
      </div>

      <style>{`
        .payment-step {
          width: 100%;
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px;
        }

        .step-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .step-header h2 {
          color: #2196F3;
          margin-bottom: 8px;
          font-size: 2rem;
          font-weight: 600;
        }

        .step-header p {
          color: #6c757d;
          font-size: 1.1rem;
        }

        .payment-content {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 32px;
          margin: 0 auto 32px auto;
          max-width: 900px;
          justify-content: center;
        }

        .payment-form {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .payment-summary {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          border-left: 4px solid #2196F3;
        }

        .payment-summary h3 {
          margin: 0 0 16px 0;
          color: #2196F3;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .summary-item {
          display: flex;
          justify-content: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
        }

        .summary-item:last-child {
          border-bottom: none;
        }

        .payment-section h3 {
          color: #495057;
          margin-bottom: 24px;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .payment-token-section {
          margin-bottom: 24px;
        }

        .card-section {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #e9ecef;
        }

        .card-label {
          display: block;
          margin-bottom: 12px;
          font-weight: 600;
          color: #495057;
          font-size: 1rem;
        }

        .token-help-text {
          margin-top: 8px;
          font-size: 0.875rem;
          color: #6c757d;
          font-style: italic;
        }

        .card-element-container {
          background: white;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 16px;
          transition: border-color 0.2s;
        }

        .card-element-container:focus-within {
          border-color: #2196F3;
        }

        .payment-token-section {
          margin-top: 20px;
        }

        .token-label {
          margin-bottom: 6px;
          font-weight: 500;
          color: #495057;
          display: block;
          font-size: 1rem;
        }

        .token-input-container {
          position: relative;
        }

        .token-input {
          width: 100%;
          padding: 12px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s;
          background: white;
          font-family: inherit;
        }

        .token-input:focus {
          outline: none;
          border-color: #2196F3;
        }

        .token-input.valid {
          border-color: #28a745;
          background-color: #f8fff9;
        }

        .token-input.invalid {
          border-color: #dc3545;
          background-color: #fff8f8;
        }

        .token-input:disabled {
          background-color: #f8f9fa;
          cursor: not-allowed;
        }

        .token-status {
          margin-top: 8px;
          min-height: 20px;
        }

        .validating {
          color: #6c757d;
          font-size: 14px;
        }

        .valid-token {
          color: #28a745;
          font-weight: 600;
          font-size: 14px;
        }

        .invalid-token {
          color: #dc3545;
          font-weight: 600;
          font-size: 14px;
        }

        .payment-error {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #dc3545;
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          border-radius: 8px;
          padding: 12px;
          margin-top: 12px;
        }

        .error-icon {
          font-size: 1.2rem;
        }

        .payment-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin: 24px auto 0 auto;
          max-width: 568px;
        }

        .btn {
          padding: 14px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .btn-primary {
          background: #2196F3;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #1976D2;
          transform: translateY(-1px);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #5a6268;
          transform: translateY(-1px);
        }

        .btn-payment {
          font-size: 1.1rem;
          font-weight: 600;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .security-notice {
          display: flex;
          gap: 16px;
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          margin-top: 24px;
          border: 1px solid #e9ecef;
        }

        .security-icons {
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 1.5rem;
        }

        .security-text p {
          margin: 0 0 8px 0;
          color: #495057;
        }

        .security-text p:last-child {
          margin-bottom: 0;
          font-size: 0.9rem;
          color: #6c757d;
        }

        .powered-by-stripe {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 12px;
        }

        .powered-text {
          font-size: 0.8rem;
          color: #8898aa;
          font-weight: 400;
        }

        .stripe-logo {
          height: 18px;
          width: auto;
          opacity: 0.8;
        }

        .what-happens-next {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid #e9ecef;
        }

        .what-happens-next h4 {
          margin: 0 0 12px 0;
          color: #28a745;
          font-size: 1rem;
          font-weight: 600;
        }

        .what-happens-next ul {
          margin: 0;
          padding-left: 0;
          list-style: none;
        }

        .what-happens-next li {
          margin-bottom: 8px;
          color: #495057;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        .what-happens-next li:last-child {
          margin-bottom: 0;
        }

        .pricing-sidebar {
          position: sticky;
          top: 20px;
          height: fit-content;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        @media (max-width: 768px) {
          .payment-content {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .payment-actions {
            flex-direction: column;
          }

          .payment-step {
            padding: 16px;
          }

          .step-header h2 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

const PaymentStep: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm />
    </Elements>
  );
};

export default PaymentStep;
